import { useEffect, useMemo, useState } from 'react';
import { composerRelanceReglementTexte, LIBELLE_LANGUE } from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useRelancerReglement, useReglages } from '../api/hooks';
import type { CommandeListee } from '../api/types';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';

/**
 * Relecture d'une relance de règlement avant envoi.
 *
 * Même chemin que les autres mails au client : composé ici d'après le modèle
 * de Réglages, dans la langue du client, relu, puis envoyé depuis contact@ au
 * clic sur « Envoyer ». Cette fenêtre est la seule relecture.
 */
export function RelanceReglement({
  commande, onFermer,
}: {
  commande: CommandeListee;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  // Les réglages portent les modèles : sans eux, l'écran montrerait le texte
  // du code pendant que Réglages en dit un autre.
  const { data: reglages } = useReglages();
  const relancer = useRelancerReglement();

  const modele = useMemo(() => composerRelanceReglementTexte({
    ref: commande.ref,
    client: commande.client,
    ville: commande.ville,
    resteDu: commande.paiement?.resteDu ?? null,
    montant: commande.montant,
    langue: commande.langue,
  }, reglages), [commande, reglages]);

  const [to, setTo] = useState(commande.email ?? '');
  const [objet, setObjet] = useState(modele.objet);
  const [corps, setCorps] = useState(modele.corps);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setTo(commande.email ?? ''); setObjet(modele.objet); setCorps(modele.corps);
  }, [modele, commande.email]);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  async function deposer() {
    setErreur(null);
    try {
      const r = await relancer.mutateAsync({
        id: commande.id,
        corps: { destinataire: to, objet, message: corps },
      });
      if (!r.brouillon.depose) {
        setErreur(r.brouillon.erreur ?? "Le mail n'a pas pu être envoyé.");
        return;
      }
      ajouter(
        r.brouillon.simule
          ? 'Relance composée, mais Outlook est en mode local : aucun mail envoyé.'
          : `Relance de règlement envoyée à ${commande.client}.`,
        r.brouillon.simule ? 'bad' : 'ok',
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'envoi a échoué.");
    }
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Relance de règlement ${commande.ref}`}>
        <div className="mhead">
          <h2>Relance de règlement — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <div className="field">
            <label htmlFor="rr-to">
              Destinataire
              <span className="tag" style={{ marginLeft: 8 }}>{LIBELLE_LANGUE[modele.langue]}</span>
            </label>
            <input
              id="rr-to" type="text" value={to} placeholder="adresse du client"
              onChange={(e) => setTo(e.target.value)}
            />
            {!commande.email && (
              <p className="err">
                Cette commande n&apos;a pas d&apos;adresse client : saisissez-la, sans quoi le
                mail ne peut pas partir.
              </p>
            )}
          </div>

          <div className="field">
            <label htmlFor="rr-obj">Objet</label>
            <input id="rr-obj" value={objet} onChange={(e) => setObjet(e.target.value)} />
          </div>

          <div className="field">
            <label htmlFor="rr-corps">Message</label>
            <textarea id="rr-corps" rows={14} value={corps} onChange={(e) => setCorps(e.target.value)} />
            <p className="indice">
              {commande.paiement?.resteDu != null
                ? 'Le montant est le reste dû relevé chez Sellsy.'
                : 'Sellsy n’a pas donné de reste dû : le montant est celui de la commande.'}
            </p>
          </div>

          {erreur && <p className="err" role="alert">{erreur}</p>}

          <p style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Le mail <strong>part dès que vous cliquez sur Envoyer</strong>, depuis contact@ :
            relisez-le ici, il n&apos;y a pas d&apos;autre relecture.
          </p>
        </div>

        <div className="mfoot">
          <Repousser commande={commande} />
          <button type="button" className="btn" onClick={onFermer}>Annuler</button>
          <button
            type="button" className="btn pri" disabled={relancer.isPending}
            onClick={() => void deposer()}
          >
            {relancer.isPending ? <><span className="spin" /> Envoi…</> : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
}
